import { Col } from 'antd'
import { Formik } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as Yup from 'yup'
import { PerCardWithAvatar } from '../../components/custom_components/CardWithAvatar'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import PerFlexRow from '../../components/custom_components/FlexRow'
import MeetingBackground from '../../components/custom_components/Meeting/MeetingBackground'
import RichTextBox from '../../components/custom_components/RichTextBox'
import PerSpacer from '../../components/custom_components/Spacer'
import PerText from '../../components/custom_components/Text'
import { FormItem } from '../../components/formik-component'
import { OtherMeetingConfirmAsManager } from '../../graphql/meeting/hooks/useOtherMeeting'
import { useCollapse } from '../../routes/collapse-context'
import { FullMeetingFragment } from '../../types'
import { MeetingConfirmBtn } from '../appraisal/appraisal-flow/shared/MeetingStepButton'
import { AgendaOtherMeeting } from './AgendaOtherMeeting'
import { HintsOtherMeeting } from './HintsOtherMeeting'
import { OtherMeetingHeader } from './OtherMeetingHeader'
import { showNotification } from '../../components/custom_components/Notification/notification'

interface Props {
  meeting: FullMeetingFragment
}
const OtherMeetingView: FC<Props> = ({ meeting }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const collapsed = useCollapse()

  const confirmOtherMeeting = async (textAreaValue: string) => {
    try {
      await OtherMeetingConfirmAsManager(meeting, textAreaValue)
    } catch (err) {
      showNotification({
        type: 'error',
        message: t(`otherMeetings.notifications.error.title`),
        description: t(`otherMeetings.notifications.error.description`),
      })
    }
  }

  const redirect = () => {
    history.push('/meetings')
  }

  const employeePrepared = !!meeting.other && !!meeting.other.notesEmployee
  const managerPrepared = !!meeting.other && !!meeting.other.notesManager
  const showNotes = employeePrepared || managerPrepared

  const renderName = (name, type: 'lastName' | 'firstName') => {
    if (!!name) {
      if (type === 'firstName') {
        return name.chosenFirstName ? name.chosenFirstName : name.legalFirstName
      } else if (type === 'lastName') {
        return name.chosenLastName ? name.chosenLastName : name.legalLastName
      }
    }
    return ''
  }

  return (
    <Formik
      initialValues={{ textAreaValue: '' }}
      enableReinitialize={true}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true)
        await confirmOtherMeeting(values.textAreaValue)
        resetForm()
        setSubmitting(false)
        redirect()
      }}
      validateOnChange={true}
      validationSchema={Yup.object().shape({
        textAreaValue: Yup.string()
          .min(2, t('formError.commentLength'))
          .required(t('formError.required', { fieldName: t('otherMeetings.protocol.headline') })),
      })}
    >
      {(props) => {
        const { isSubmitting, handleSubmit, values, isValid, initialValues } = props
        const isViolatingForm = !isValid || initialValues.textAreaValue === values.textAreaValue
        return (
          <>
            <ContentContainer collapsed={collapsed}>
              <OtherMeetingHeader meeting={meeting} isPreparation={false} />
              <MeetingBackground withHeader>
                <PerText appearance="tile-heading">{t(`meetingTypes.${meeting.type}`)}</PerText>

                <PerSpacer mt="m">
                  <HintsOtherMeeting meeting={meeting} className="otherMeetings-collapse" />
                </PerSpacer>

                {showNotes && (
                  <PerSpacer mt="xxxl">
                    <div data-e2e-other-meeting-notes>
                      <PerText appearance="tile-heading" mb="s">
                        {t('otherMeetings.notes.headline')}
                      </PerText>
                      <PerFlexRow>
                        {employeePrepared && (
                          <Col span={12}>
                            <PerCardWithAvatar
                              nameType="firstName"
                              employeeId={meeting.employee.id}
                              firstName={renderName(meeting.employee, 'firstName')}
                              lastName={renderName(meeting.employee, 'lastName')}
                            >
                              <PerText appearance="quote">
                                <RichTextBox
                                  text={meeting.other && meeting.other.notesEmployee ? meeting.other.notesEmployee : ''}
                                  readonly
                                />
                              </PerText>
                            </PerCardWithAvatar>
                          </Col>
                        )}
                        {managerPrepared && (
                          <Col span={12}>
                            <PerCardWithAvatar
                              nameType="you"
                              employeeId={meeting.reviewer ? meeting.reviewer.id : null}
                              firstName={meeting.reviewer ? meeting.reviewer.firstName : ''}
                              lastName={meeting.reviewer ? meeting.reviewer.lastName : ''}
                            >
                              <PerText appearance="quote">
                                <RichTextBox
                                  text={meeting.other && meeting.other.notesManager ? meeting.other.notesManager : ''}
                                  readonly
                                />
                              </PerText>
                            </PerCardWithAvatar>
                          </Col>
                        )}
                      </PerFlexRow>
                    </div>
                  </PerSpacer>
                )}

                <PerSpacer mt="xxxl">
                  <PerFlexRow>
                    <Col span={12}>
                      <PerText appearance="tile-heading" mb="s">
                        {t('otherMeetings.protocol.headline')}
                      </PerText>
                    </Col>
                    {!!meeting.other && !!meeting.other.agenda && (
                      <Col span={12}>
                        <PerText appearance="tile-heading" mb="s">
                          {t('otherMeetings.agendaHeadline')}
                        </PerText>
                      </Col>
                    )}
                  </PerFlexRow>
                  <PerFlexRow>
                    <Col span={12}>
                      <FormItem name="textAreaValue">
                        <RichTextBox
                          placeholder={t('otherMeetings.protocol.placeholder')}
                          data-e2e-other-meeting-protocol
                          text={values.textAreaValue}
                          onChange={(text) => props.setFieldValue('textAreaValue', text)}
                        />
                      </FormItem>
                    </Col>
                    {!!meeting.other && !!meeting.other.agenda && (
                      <Col span={12}>
                        <AgendaOtherMeeting meeting={meeting} />
                      </Col>
                    )}
                  </PerFlexRow>
                </PerSpacer>
              </MeetingBackground>

              <PerFlexRow justify="end">
                <MeetingConfirmBtn disabled={isViolatingForm || isSubmitting} onMouseDown={() => handleSubmit()} />
              </PerFlexRow>
            </ContentContainer>
          </>
        )
      }}
    </Formik>
  )
}

export default OtherMeetingView
